import stepOneImage from "assets/img/tutorial/1.jpg";
import stepOneONImage from "assets/img/tutorial/1_ON.jpg";
import stepTwoImage from "assets/img/tutorial/2.jpg";
import stepTwoONImage from "assets/img/tutorial/2_ON.jpg";
import stepThreeImage from "assets/img/tutorial/3.jpg";
import stepFourImage from "assets/img/tutorial/4.jpg";
import {ButtonWithIcon} from "components/ButtonWithIcon";
import {Exist} from "components/Exist";
import {HOCModal} from "components/HOCModal";
import {HOCModalCenterDesktopPosition} from "components/HOCModalCenterDesktopPosition";
import {setForceTutorialModal, setTutorialViewState} from "modules/actions/user";
import {RequestState} from "modules/enums";
import {
	getIsOntarioBetting,
	isTutorialOpenForced,
	isTutorialViewed,
	loadingUserState,
} from "modules/selectors";
import {useAdobeDTM, useIsMobile} from "modules/utils/hooks";
import React, {Fragment, useEffect, useState} from "react";
import Swipe from "react-easy-swipe";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import styled from "@emotion/styled";

const Wrapper = styled.div`
	overflow-y: scroll;
	overflow-x: hidden;
`;

const PreviewWrapper = styled.div<{image: string}>`
	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	margin-top: 20px;
	height: 400px;
	overflow: hidden;
	background: url("${(props) => props.image}") center top no-repeat;
	background-size: contain;
	opacity: 0;
	animation: fadeIn 0.5s ease forwards;

	&:before {
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 100px;
		content: "";
		display: inline-block;
		background: linear-gradient(to top, #090b0f, transparent);
	}
`;

const Controls = styled.div`
	padding: 30px 40px;
`;

const Title = styled.p`
	font-weight: 800;
	font-size: 30px;
	line-height: 28px;
	text-align: center;
`;

const Description = styled.p`
	padding: 20px 0;
	text-align: center;
	color: #848990;
	font-size: 14px;
`;

const Buttons = styled.div`
	display: flex;
	position: relative;
	bottom: 20px;
	button {
		&:first-of-type {
			margin-right: 10px;
		}
	}
`;

const NextButton = styled.button`
	background: #2687ff;
	color: #ffffff;
`;

const SkipButton = styled(NextButton)`
	background: #282f38;
`;

const Dots = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 30px 0;
	width: 100%;
`;

const Dot = styled.div<{isActive: boolean}>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: ${(props) => (props.isActive ? "#fff" : "#7d7d7d")};
	opacity: 1;
	margin-right: 7px;

	&:last-of-type {
		margin-right: 0;
	}
`;

/* eslint-disable sonarjs/no-duplicate-string */
const adobeDTM = {
	step1: {
		trackState: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"page.subType": "how to play beat the streak",
			"page.viewType": "beat the streak and win $5.6 million",
		},
		exitAction: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"action.name": "mlb play : beat the streak : how to play beat the streak : exit click",
		},
		skipAction: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"action.name":
				"mlb play : beat the streak : how to play beat the streak : beat the streak and win $5.6 million : skip click",
			"action.element.text": "skip",
		},
		nextAction: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"action.name":
				"mlb play : beat the streak : how to play beat the streak : beat the streak and win $5.6 million : next click",
			"action.element.text": "next",
		},
		backAction: undefined,
	},
	step2: {
		trackState: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"page.subType": "how to play beat the streak",
			"page.viewType": "make a pick for an eligibile day",
		},
		exitAction: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"action.name": "mlb play : beat the streak : how to play beat the streak : exit click",
		},
		nextAction: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"action.name":
				"mlb play : beat the streak : how to play beat the streak : make a pick for an eligibile day : next click",
			"action.element.text": "next",
		},
		skipAction: undefined,
		backAction: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"action.name":
				"mlb play : beat the streak : how to play beat the streak : make a pick for an eligibile day : back click",
			"action.element.text": "back",
		},
	},
	step3: {
		trackState: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"page.subType": "how to play beat the streak",
			"page.viewType": "pick a batter you think will get a hit",
		},
		exitAction: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"action.name": "mlb play : beat the streak : how to play beat the streak : exit click",
		},
		skipAction: undefined,
		nextAction: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"action.name":
				"mlb play : beat the streak : how to play beat the streak : pick a batter you think will get a hit : next click",
			"action.element.text": "next",
		},
		backAction: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"action.name":
				"mlb play : beat the streak : how to play beat the streak : pick a batter you think will get a hit : back click",
			"action.element.text": "back",
		},
	},
	step4: {
		trackState: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"page.subType": "how to play beat the streak",
			"page.viewType": "double down to build your streak faster",
		},
		exitAction: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"action.name": "mlb play : beat the streak : how to play beat the streak : exit click",
		},
		skipAction: undefined,
		nextAction: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"action.name":
				"mlb play : beat the streak : how to play beat the streak : double down to build your streak faster : play click",
			"action.element.text": "next",
		},
		backAction: {
			"page.name": "mlb play : beat the streak : how to play beat the streak",
			"action.name":
				"mlb play : beat the streak : how to play beat the streak : double down to build your streak faster : back click",
			"action.element.text": "back",
		},
	},
};
/* eslint-enable sonarjs/no-duplicate-string */

/** Remove once this PR will be merged https://github.com/leandrowd/react-easy-swipe/pull/72 **/
declare module "react-easy-swipe" {
	/* eslint-disable @typescript-eslint/naming-convention */
	interface SwipeProps {
		// workaround for https://github.com/leandrowd/react-easy-swipe/pull/64
		children?: React.ReactNode;
	}
}

enum FlowDirrection {
	LEFT,
	RIGHT,
}
export const TutorialModal: React.FC = () => {
	const {trackAction, trackState} = useAdobeDTM();

	const dispatch = useDispatch();
	const [currentStep, setCurrentStep] = useState(0);
	const isViewed = useSelector(isTutorialViewed);
	const isForcedToView = useSelector(isTutorialOpenForced);
	const loadState = useSelector(loadingUserState);
	const i18n = useTranslation();
	const isMobile = useIsMobile();
	const isONBetting = useSelector(getIsOntarioBetting);
	const [flowDirection, setFlowDirection] = useState(FlowDirrection.RIGHT);

	const steps = [
		{
			image: isONBetting ? stepOneONImage : stepOneImage,
			imageLabel: i18n.t("tutorial.step1.image_label", "Home screen with a player"),
			title: i18n.t("tutorial.step1.title"),
			description: i18n.t("tutorial.step1.description"),
			...adobeDTM.step1,
		},
		{
			image: isONBetting ? stepTwoONImage : stepTwoImage,
			imageLabel: i18n.t("tutorial.step2.image_label", "Home screen"),
			title: i18n.t("tutorial.step2.title"),
			description: i18n.t("tutorial.step2.description"),
			...adobeDTM.step2,
		},
		{
			image: stepThreeImage,
			title: i18n.t("tutorial.step3.title"),
			imageLabel: i18n.t("tutorial.step3.image_label", "Players list"),
			description: i18n.t("tutorial.step3.description"),
			...adobeDTM.step3,
		},
		{
			image: stepFourImage,
			title: i18n.t("tutorial.step4.title"),
			imageLabel: i18n.t("tutorial.step4.image_label", "Home screen with a player"),
			description: i18n.t("tutorial.step4.description"),
			...adobeDTM.step4,
		},
	];

	const currentStepObject = steps[currentStep];

	const isOpen = [isViewed, !isForcedToView].every(Boolean);
	const isHidden = [isOpen, loadState !== RequestState.Received].some(Boolean);

	useEffect(() => {
		if (!isHidden) {
			trackState(currentStepObject.trackState);
		}
	}, [currentStep, isHidden]);

	if (isHidden) {
		return null;
	}

	const isLastStep = currentStep === steps.length - 1;

	const onSkip = () => {
		if (isForcedToView) {
			dispatch(setForceTutorialModal(false));
		} else {
			dispatch(setTutorialViewState());
		}

		setCurrentStep(0);
	};

	const onSwipeLeft = () => {
		if (isLastStep) {
			onSkip();
		} else {
			trackAction(currentStepObject.nextAction);
			setCurrentStep(currentStep + 1);
			setFlowDirection(FlowDirrection.LEFT);
		}
	};

	const onSwipeRight = () => {
		if (currentStep === 0) {
			onSkip();
		} else {
			trackAction(currentStepObject.backAction);
			setCurrentStep(currentStep - 1);
			setFlowDirection(FlowDirrection.RIGHT);
		}
	};

	const onSkipClick = () => {
		trackAction(currentStepObject.skipAction);
		onSkip();
	};

	const onCloseSkip = () => {
		trackAction(currentStepObject.exitAction);
		onSkip();
	};

	const onPlayClick = () => {
		trackAction(currentStepObject.nextAction);
		onSkip();
	};

	const Content = () => (
		<Wrapper>
			<Swipe onSwipeLeft={onSwipeLeft} onSwipeRight={onSwipeRight} tolerance={100}>
				<PreviewWrapper
					key={currentStep}
					image={currentStepObject.image}
					role={"img"}
					aria-label={currentStepObject.imageLabel}
				/>

				<Controls>
					<Title>{currentStepObject.title}</Title>

					<Description>{currentStepObject.description}</Description>

					<Buttons>
						<Exist when={!currentStep}>
							<ButtonWithIcon
								text={i18n.t("tutorial.buttons.skip")}
								styleExtend={SkipButton}
								onClick={onSkipClick}
								withAutoFocus={flowDirection === FlowDirrection.RIGHT}
							/>
						</Exist>
						<Exist when={!!currentStep}>
							<ButtonWithIcon
								text={i18n.t("tutorial.buttons.back")}
								styleExtend={SkipButton}
								onClick={onSwipeRight}
								withAutoFocus={flowDirection === FlowDirrection.RIGHT}
							/>
						</Exist>

						<Exist when={isLastStep}>
							<ButtonWithIcon
								text={i18n.t("tutorial.buttons.play")}
								styleExtend={NextButton}
								onClick={onPlayClick}
								withAutoFocus={flowDirection === FlowDirrection.LEFT}
							/>
						</Exist>
						<Exist when={!isLastStep}>
							<ButtonWithIcon
								text={i18n.t("tutorial.buttons.next")}
								styleExtend={NextButton}
								onClick={onSwipeLeft}
								withAutoFocus={flowDirection === FlowDirrection.LEFT}
							/>
						</Exist>
					</Buttons>

					<Dots>
						{steps.map((step, index) => (
							<Dot key={index} isActive={index === currentStep} />
						))}
					</Dots>
				</Controls>
			</Swipe>
		</Wrapper>
	);

	const props = {
		isOpen: true,
		onClose: onCloseSkip,
		name: "tutorial",
		headerText: (
			<Fragment>
				{i18n.t("tutorial.header.title_part_one")}
				<br />
				{i18n.t("tutorial.header.title_part_two")}
			</Fragment>
		),
	};

	return (
		<Fragment>
			<Exist when={isMobile}>
				<HOCModal {...props}>
					<Content />
				</HOCModal>
			</Exist>
			<Exist when={!isMobile}>
				<HOCModalCenterDesktopPosition {...props}>
					<Content />
				</HOCModalCenterDesktopPosition>
			</Exist>
		</Fragment>
	);
};
